import type { ApiQuery, ApiResponse, MutationResult } from '../../api/contract';
import type { ApiClient, RequestOptions } from '../../api/http';
import type { AgentRepository } from './repository';
import type { AgentListQuery } from './types';

type AgentListResponse = ApiResponse<'/api/v1/agents', 'get'>;
type AgentResponse = ApiResponse<'/api/v1/agents/{id}', 'get'>;

export function createRealAgentRepository(
  apiClient: ApiClient,
  accessToken: string | null,
): AgentRepository {
  const auth: RequestOptions = accessToken === null ? {} : { accessToken };

  return {
    async list(query, signal) {
      const result = await apiClient.get<AgentListResponse>('/api/v1/agents', {
        ...auth,
        query: toListQuery(query),
        signal,
      });
      return {
        items: result.data.data,
        total: result.data.pagination.total,
        requestId: result.requestId,
      };
    },
    async get(agentId, signal) {
      const result = await apiClient.get<AgentResponse>(agentPath(agentId), { ...auth, signal });
      return result.data.data;
    },
    async create(input) {
      const result = await apiClient.post<ApiResponse<'/api/v1/agents', 'post'>>(
        '/api/v1/agents',
        input,
        auth,
      );
      return mutation(result);
    },
    async update(agentId, input) {
      const result = await apiClient.patch<ApiResponse<'/api/v1/agents/{id}', 'patch'>>(
        agentPath(agentId),
        input,
        auth,
      );
      return mutation(result);
    },
    async setDefaultUpline(agentId, input) {
      const result = await apiClient.post<
        ApiResponse<'/api/v1/agents/{id}/default-upline', 'post'>
      >(`${agentPath(agentId)}/default-upline`, input, auth);
      return mutation(result);
    },
    async createHouseMember(agentId, input) {
      const result = await apiClient.post<ApiResponse<'/api/v1/agents/{id}/house-members', 'post'>>(
        `${agentPath(agentId)}/house-members`,
        input,
        auth,
      );
      return mutation(result);
    },
    async changeStatus(agentId, input) {
      const result = await apiClient.post<ApiResponse<'/api/v1/agents/{id}/status', 'post'>>(
        `${agentPath(agentId)}/status`,
        input,
        auth,
      );
      return mutation(result);
    },
  };
}

function toListQuery(query: AgentListQuery): ApiQuery<'/api/v1/agents', 'get'> {
  return {
    page: query.page,
    pageSize: query.pageSize,
    sort: query.sort,
    order: query.order,
    ...(query.filters.partnerId.length === 0 ? {} : { partnerId: query.filters.partnerId }),
    ...(query.filters.code.length === 0 ? {} : { code: query.filters.code }),
    ...(query.filters.name.length === 0 ? {} : { name: query.filters.name }),
    ...(query.filters.status === '' ? {} : { status: query.filters.status }),
  };
}

function mutation<T>(result: {
  readonly data: { readonly data: T };
  readonly requestId: string | null;
}): MutationResult<T> {
  return { data: result.data.data, requestId: result.requestId };
}

function agentPath(agentId: string): string {
  return `/api/v1/agents/${encodeURIComponent(agentId)}`;
}
